/**
 * 连接状态指示器
 *
 * 显示 Electric 实时同步的连接状态，使用颜色区分：
 * - 绿色：已连接服务器，实时同步中
 * - 黄色：正在尝试连接服务器
 * - 红色：离线状态
 * - 橙色：连接异常
 */

import React, { useState } from 'react'
import { useElectricSync } from '../hooks/useElectricSync'
import { useAuthStore } from '../auth/store'
import { useSyncStatusContext } from '../contexts/SyncStatusContext'

type ConnectionStatus = 'connecting' | 'connected' | 'offline' | 'error'

export const SyncStatusIndicator: React.FC = () => {
  const { onRetry, onUseOffline } = useSyncStatusContext()
  const { user } = useAuthStore()
  const userId = user?.id || null

  const { isInitialized, isConnected, error, lastSyncAt } = useElectricSync({
    userId,
    enabled: !!userId,
  })

  const [isHovered, setIsHovered] = useState(false)

  // 确定连接状态 - 修复：错误状态优先显示，不依赖 isInitialized
  let status: ConnectionStatus
  if (error) {
    status = 'error'
  } else if (!isInitialized) {
    status = 'connecting'
  } else if (!isConnected) {
    status = 'offline'
  } else {
    status = 'connected'
  }

  // （离线/恢复的自动弹出提示已移除：统一由全局 ReadonlyModeBanner 常驻横幅呈现；
  //   本组件仅保留桌面端悬停查看）

  // 格式化时间
  const formatTime = (date: Date | null): string => {
    if (!date) return ''
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    if (diff < 5000) return '刚刚'
    if (diff < 60000) return `${Math.floor(diff / 1000)}秒前`
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  }

  // 渲染不同状态的 tooltip 内容
  const renderTooltipContent = () => {
    switch (status) {
      case 'connected':
        return (
          <>
            <div className="font-medium text-gray-900 dark:text-white mb-2">连接状态：已连接</div>
            <div className="text-gray-600 dark:text-gray-400 text-xs space-y-1">
              <p>• 已连接服务器，数据实时同步中</p>
              <p>• 您的编辑内容将自动同步到所有设备</p>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-gray-500 dark:text-gray-400 text-xs">
                上次同步：{lastSyncAt ? formatTime(lastSyncAt) : '刚刚'}
              </p>
            </div>
          </>
        )
      case 'connecting':
        return (
          <>
            <div className="font-medium text-gray-900 dark:text-white mb-2">连接状态：连接中</div>
            <div className="text-gray-600 dark:text-gray-400 text-xs space-y-1">
              <p>• 正在尝试连接服务器，请稍候...</p>
              <p>• 如果长时间未连接，请检查网络连接</p>
            </div>
          </>
        )
      case 'offline':
        return (
          <>
            <div className="font-medium text-gray-900 dark:text-white mb-2">连接状态：离线</div>
            <div className="text-gray-600 dark:text-gray-400 text-xs space-y-1 mb-3">
              <p>• 当前处于离线状态</p>
              <p>• 不建议离线期间编辑内容，不确定能否在恢复后完成同步上传！</p>
            </div>
            {onRetry && (
              <div className="flex gap-2">
                <button
                  onClick={() => { onRetry(); setIsHovered(false) }}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded transition-colors"
                >
                  重试连接
                </button>
                {onUseOffline && (
                  <button
                    onClick={() => { onUseOffline(); setIsHovered(false) }}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    离线使用
                  </button>
                )}
              </div>
            )}
          </>
        )
      case 'error':
        return (
          <>
            <div className="font-medium text-gray-900 dark:text-white mb-2">连接状态：异常</div>
            <div className="text-gray-600 dark:text-gray-400 text-xs space-y-1 mb-3">
              <p>• 连接服务器时出现异常</p>
              <p>• 错误信息：{error?.message || '未知错误'}</p>
              <p>• 请尝试重启应用或检查网络连接</p>
            </div>
            {onRetry && (
              <div className="flex gap-2">
                <button
                  onClick={() => { onRetry(); setIsHovered(false) }}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded transition-colors"
                >
                  重试连接
                </button>
                {onUseOffline && (
                  <button
                    onClick={() => { onUseOffline(); setIsHovered(false) }}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    离线使用
                  </button>
                )}
              </div>
            )}
          </>
        )
    }
  }

  // 状态配置
  const statusConfig = {
    connected: {
      color: 'bg-green-500',
      text: '已连接',
      pulse: false,
    },
    connecting: {
      color: 'bg-yellow-500',
      text: '连接中',
      pulse: true,
    },
    offline: {
      color: 'bg-red-500',
      text: '离线',
      pulse: false,
    },
    error: {
      color: 'bg-orange-500',
      text: '异常',
      pulse: false,
    },
  }

  const config = statusConfig[status]

  return (
    <div
      className="relative flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 状态指示灯 */}
      <div
        className={`w-2 h-2 rounded-full ${config.color} ${
          config.pulse ? 'animate-pulse' : ''
        }`}
      />

      {/* 状态文字 - 始终显示 */}
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {config.text}
      </span>

      {/* Tooltip */}
      {isHovered && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 z-50 animate-in fade-in duration-200">
          {renderTooltipContent()}
        </div>
      )}
    </div>
  )
}

export default SyncStatusIndicator
